import { useMemo, useEffect, useRef } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { Accordion } from 'src/components/UI/Accordion';
import { Controller, useFormContext } from 'react-hook-form';
import { errorMessageSx } from 'src/components/UI/Input.styles';
import {
  businessImagesCardSx,
  businessImagesImageSx,
  businessImagesPlaceholderSx,
  businessImagesTitleSx
} from '../../styles/createCampaign';
import { ConfirmAndPayFormData } from './index';

const BUSINESS_IMAGES_CONFIG = [
  {
    title: 'Profile Image',
    defaultImage: ''
  },
  {
    title: 'Cover Image',
    defaultImage: ''
  },
  {
    title: 'Additional Image',
    defaultImage: ''
  },
  {
    title: 'Additional Image',
    defaultImage: ''
  }
];

export const BusinessImages = () => {
  const {
    control,
    watch,
    setError,
    clearErrors,
    formState: { errors }
  } = useFormContext<ConfirmAndPayFormData>();
  const businessImages = watch('businessImages');

  // Track previous URLs for cleanup
  const previousUrlsRef = useRef<string[]>([]);

  // Create preview URLs from File objects
  const previewUrls = useMemo(() => {
    return businessImages.map((imageData, index) => {
      const file = imageData?.file;
      if (file instanceof File) {
        return URL.createObjectURL(file);
      }
      // Return default image for Profile Image (index 0) if no file
      if (index === 0 && !file) {
        return BUSINESS_IMAGES_CONFIG[0].defaultImage;
      }
      return '';
    });
  }, [businessImages]);

  // Cleanup object URLs when files change or component unmounts
  useEffect(() => {
    const previousUrls = previousUrlsRef.current;
    
    // Revoke URLs that are no longer in use
    previousUrls.forEach((url) => {
      if (url && url.startsWith('blob:') && !previewUrls.includes(url)) {
        URL.revokeObjectURL(url);
      }
    });

    // Update ref for next cleanup
    previousUrlsRef.current = previewUrls;

    // Cleanup on unmount
    return () => {
      previewUrls.forEach((url) => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [previewUrls]);

  // Validate if file is an image type
  const isValidImageFile = (file: File): boolean => {
    const validImageTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/svg+xml',
    ];

    // Check MIME type
    if (!validImageTypes.includes(file.type.toLowerCase())) {
      return false;
    }

    // Also check file extension as a fallback
    const fileName = file.name.toLowerCase();
    const validExtensions = [
      '.jpg',
      '.jpeg',
      '.png',
      '.webp',
      '.svg',
    ];
    const hasValidExtension = validExtensions.some((ext) =>
      fileName.endsWith(ext)
    );

    return hasValidExtension;
  };

  const handleImageChange = (
    index: number,
    file: File | null,
    onChange: (images: Array<{ file: File | null; label: string }>) => void
  ) => {
    const currentImages = [...(businessImages || [])];
    const currentLabel =
      currentImages[index]?.label || BUSINESS_IMAGES_CONFIG[index]?.title || '';
    currentImages[index] = { file, label: currentLabel };
    onChange(currentImages);
  };

  return (
    <Accordion title="Business Images" defaultExpanded={true}>
      <Controller
        name="businessImages"
        control={control}
        render={({ field }) => (
          <Grid container spacing={2}>
            {BUSINESS_IMAGES_CONFIG.map((config, index) => {
              const imageSrc = previewUrls[index];

              return (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Box>
                    <Typography sx={businessImagesTitleSx}>
                      {config.title}
                    </Typography>

                    <Box sx={businessImagesCardSx}>
                      <label
                        htmlFor={`business-image-${index}`}
                        style={{ cursor: 'pointer' }}
                      >
                        {imageSrc ? (
                          <Box
                            component="img"
                            src={imageSrc}
                            alt={config.title}
                            sx={businessImagesImageSx}
                          />
                        ) : (
                          <Box sx={businessImagesPlaceholderSx} />
                        )}
                      </label>
                      <input
                        id={`business-image-${index}`}
                        type="file"
                        accept="image/*"
                        aria-label={config.title}
                        style={{ display: 'none' }}
                        onChange={(event) => {
                          const selectedFile = event.target.files?.[0];
                          // Only update if a file was actually selected (not canceled)
                          if (selectedFile) {
                            // Validate that the file is an image type
                            if (isValidImageFile(selectedFile)) {
                              // Clear any previous errors for this field
                              clearErrors(
                                `businessImages.${index}.file` as any
                              );
                              handleImageChange(
                                index,
                                selectedFile,
                                field.onChange
                              );
                            } else {
                              // Set error using react-hook-form
                              setError(
                                `businessImages.${index}.file` as any,
                                {
                                  type: 'manual',
                                  message: `Please select a valid file. Selected file: ${selectedFile.name}`
                                },
                                { shouldFocus: false }
                              );
                            }
                          }
                          // Reset the input value so the same file can be selected again
                          event.target.value = '';
                        }}
                      />
                    </Box>
                    {errors.businessImages?.[index]?.file && (
                      <Typography sx={errorMessageSx}>
                        {errors.businessImages[index]?.file?.message as string}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
            <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: { xs: '12px',},
                  lineHeight: { xs: '18px',},
                  color: 'var(--color-text-purple)',
                  marginTop: '8px'
                }}
              >
                Note: Valid image formats are PNG, JPG, JPEG, SVG, GIF, WEBP,
                BMP, and ICO.
              </Typography>
            </Grid>
          </Grid>
        )}
      />
    </Accordion>
  );
};
